import React, { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { createTheme, ThemeProvider } from "@mui/material";
import {
  ShoppingCart,
  TrendingUp,
  MedicalServices,
} from "@mui/icons-material";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { dataGridCellNoFocus } from "@/utils/adminTableStyles";
import { DocLinkBoxTheme, QudaBoxTheme } from "@/config/boxThemes";
import StatBox from "@/components/data-visuals/Statbox";
import QudaLogo from "@/Images/Logos/Quda_Logo.png";
import DocLinkLogo from "@/Images/Logos/DocLink_Logo.png";
import { getChemistName } from "@/utils/textUtils";
import { formatPharmacyRow } from "@/components/tables/shared/PharmacyDataGrid";

const centeredColumnProps = {
  headerAlign: "center",
  align: "center",
};

function adaptChemistDetailForPharmacyRow(cd) {
  if (!cd) return {};
  return {
    chemistID: cd.chemistID ?? cd.ChemistID,
    pharmacyName: cd.pharmacyName ?? cd.chemistName ?? cd.ChemistName,
    staffCount: cd.staffCount ?? cd.StaffCount,
    terminalCount: cd.terminalCount ?? cd.TerminalCount ?? cd.installedComputers,
    qudaStats: cd.qudaStats ?? {},
    waitingListStats: cd.waitingListStats ?? {},
    doclinkStats: cd.doclinkStats ?? {},
  };
}

const overviewColumns = [
  {
    field: "pharmacyName",
    headerName: "Pharmacy Name",
    flex: 1,
    minWidth: 200,
    ...centeredColumnProps,
    renderCell: (params) => getChemistName(params.value),
  },
  {
    field: "staffCount",
    headerName: "Registered Staff",
    width: 120,
    type: "number",
    ...centeredColumnProps,
  },
  {
    field: "terminalCount",
    headerName: "Installed Computers",
    width: 140,
    type: "number",
    ...centeredColumnProps,
  },
  {
    field: "totalOrdersEver",
    headerName: "Total Orders",
    width: 130,
    type: "number",
    ...centeredColumnProps,
  },
  {
    field: "ordersLast30Days",
    headerName: "Orders (30d)",
    width: 130,
    type: "number",
    ...centeredColumnProps,
  },
  {
    field: "totalConsultsCreated",
    headerName: "Appointments",
    width: 130,
    type: "number",
    ...centeredColumnProps,
  },
  {
    field: "weeklyAverageLast4Weeks",
    headerName: "Weekly Ave (4wks)",
    width: 160,
    type: "number",
    ...centeredColumnProps,
  },
];

const gridTheme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: "white",
          borderRadius: 12,
          border: `2px solid ${PHARMACY_HALO_COLORS.BLUE}`,
        },
        columnHeaders: {
          backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
          color: "#FFF !important",
          borderBottom: "none",
        },
        columnHeader: {
          backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
          color: "#FFF !important",
          "&:focus": { outline: "none" },
        },
        footerContainer: {
          backgroundColor: "#fff",
          borderTop: `2px solid ${PHARMACY_HALO_COLORS.RED}`,
        },
      },
    },
  },
  typography: { fontSize: 14 },
});

const AdminChemistOverviewPanel = ({ chemistData }) => {
  const transformedData = useMemo(() => {
    if (!chemistData) return [];
    return [formatPharmacyRow(adaptChemistDetailForPharmacyRow(chemistData), 0)];
  }, [chemistData]);

  const summaryStats = useMemo(() => {
    if (!transformedData.length) return null;
    const totalOrders = transformedData.reduce((s, it) => s + it.totalOrdersEver, 0);
    const totalOrders30Days = transformedData.reduce(
      (s, it) => s + it.ordersLast30Days,
      0
    );
    const totalOrdersPrevious30Days = transformedData.reduce(
      (s, it) => s + it.ordersPrevious30Days,
      0
    );
    const totalAppointments = transformedData.reduce(
      (s, it) => s + it.totalConsultsCreated,
      0
    );
    const totalAppointments30Days = transformedData.reduce(
      (s, it) => s + it.appointmentsLast30Days,
      0
    );
    const totalAppointmentsPrevious30Days = transformedData.reduce(
      (s, it) => s + it.appointmentsPrevious30Days,
      0
    );
    const appointments30DayDelta =
      totalAppointments30Days - totalAppointmentsPrevious30Days;
    const orders30DayDelta = totalOrders30Days - totalOrdersPrevious30Days;
    return {
      totalOrders,
      totalOrders30Days,
      totalOrdersPrevious30Days,
      orders30DayDelta,
      totalAppointments,
      totalAppointments30Days,
      totalAppointmentsPrevious30Days,
      appointments30DayDelta,
    };
  }, [transformedData]);

  return (
    <ThemeProvider theme={gridTheme}>
      <div className="space-y-6">
        {summaryStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatBox
              theme={QudaBoxTheme}
              logo={QudaLogo}
              title="Total Orders"
              value={summaryStats.totalOrders}
              Icon={ShoppingCart}
              valueFormatter={(v) => Number(v).toLocaleString()}
            />
            <StatBox
              theme={QudaBoxTheme}
              logo={QudaLogo}
              title="Orders (30d)"
              value={summaryStats.totalOrders30Days}
              Icon={TrendingUp}
              valueFormatter={(v) => Number(v).toLocaleString()}
              deltaValue={
                summaryStats.totalOrdersPrevious30Days > 0
                  ? (summaryStats.orders30DayDelta /
                      summaryStats.totalOrdersPrevious30Days) *
                    100
                  : 0
              }
              deltaFormatter={(v) =>
                `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`
              }
              deltaLabel="Orders"
            />
            <StatBox
              theme={DocLinkBoxTheme}
              logo={DocLinkLogo}
              title="Total Appointments"
              value={summaryStats.totalAppointments}
              Icon={MedicalServices}
              valueFormatter={(v) => Number(v).toLocaleString()}
            />
            <StatBox
              theme={DocLinkBoxTheme}
              logo={DocLinkLogo}
              title="Appointments (30d)"
              value={summaryStats.totalAppointments30Days}
              Icon={TrendingUp}
              valueFormatter={(v) => Number(v).toLocaleString()}
              deltaValue={
                summaryStats.totalAppointmentsPrevious30Days > 0
                  ? (summaryStats.appointments30DayDelta /
                      summaryStats.totalAppointmentsPrevious30Days) *
                    100
                  : 0
              }
              deltaFormatter={(v) =>
                `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`
              }
              deltaLabel="Appointments"
              deltaPositiveColor="#4ade80"
              deltaNegativeColor="#dc2626"
              deltaNeutralColor="#4ade80"
            />
          </div>
        )}

        <div
          className="bg-white rounded-lg shadow-lg border-2 overflow-hidden"
          style={{ borderColor: PHARMACY_HALO_COLORS.BLUE, borderWidth: "3px" }}
        >
          <div className="p-4" style={{ width: "100%" }}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6 mb-4">
              <div className="hidden md:block w-full md:max-w-xs" />
              <div className="flex-1 text-center">
                <h3
                  className="text-xl font-bold"
                  style={{ color: PHARMACY_HALO_COLORS.BLUE }}
                >
                  Pharmacy Data
                </h3>
              </div>
              <div className="hidden md:block w-full md:max-w-xs" />
            </div>

            <div style={{ width: "100%", height: Math.max(180, 80 + 52 * transformedData.length) }}>
              <DataGrid
                rows={transformedData}
                columns={overviewColumns}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                disableColumnReorder
                disableColumnResize
                disableMultipleSelection
                disableSelectionOnClick
                disableRowSelection
                disableVirtualization
                disableIgnoreModificationsIfProcessingProps
                hideFooter
                density="standard"
                rowHeight={52}
                headerHeight={80}
                sx={{
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #e5e7eb",
                    fontSize: 14,
                    cursor: "default",
                    userSelect: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  },
                  ...dataGridCellNoFocus,
                  "& .MuiDataGrid-cellContent": {
                    width: "100%",
                    textAlign: "center",
                  },
                  "& .MuiDataGrid-row": {
                    cursor: "default",
                    userSelect: "none",
                    "&:hover": {
                      backgroundColor: `${PHARMACY_HALO_COLORS.YELLOW} !important`,
                      opacity: 0.85,
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: PHARMACY_HALO_COLORS.LIGHT_GRAY,
                    },
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
                    color: "#FFF !important",
                    "& .MuiDataGrid-columnSeparator": {
                      color: "#FFF",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      opacity: 1,
                      color: "#FFF !important",
                      display: "block",
                    },
                    "& .MuiDataGrid-menuIconButton": {
                      color: "#FFF !important",
                    },
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: "#FFF !important",
                    fontWeight: "bold",
                    whiteSpace: "normal",
                    lineHeight: "1.2",
                    textAlign: "center",
                    padding: "4px 8px",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    cursor: "default",
                    userSelect: "none",
                    height: "auto !important",
                    minHeight: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default AdminChemistOverviewPanel;
